import { c as _c } from "react/compiler-runtime";
import { Chip } from "@heroui/react";
import * as d3 from "d3-geo";
import type React from "react";
import { useState } from "react";
import * as topojson from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
import { COUNTRIES } from "@/lib/countries";
import { useScopedI18n } from "@/lib/i18n";
interface CountryProperties {
  name: string;
}
interface WorldTopology extends Topology {
  objects: {
    countries: GeometryCollection<CountryProperties>;
  };
}
interface MapFeature {
  type: "Feature";
  id: string;
  properties: CountryProperties;
  geometry: d3.GeoGeometryObjects;
}
interface MapFeatureCollection {
  type: "FeatureCollection";
  features: MapFeature[];
}
type FilterType = "home" | "visited" | null;
export function WorldMap() {
  const $ = _c(44);
  const t = useScopedI18n("map");
  const [activeFilter, setActiveFilter] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["703", "705", "191", "203", "040", "380", "756", "276", "616", "528", "250", "100"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const VISITED_IDS = t0;
  const topology = COUNTRIES as unknown as WorldTopology;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== activeFilter || $[2] !== t) {
    const featureCollection = topojson.feature(topology, topology.objects.countries) as unknown as MapFeatureCollection;
    const projection = d3.geoMercator().fitSize([1000, 650], featureCollection as unknown as d3.ExtendedFeatureCollection);
    const pathGenerator = d3.geoPath().projection(projection);
    let t9;
    if ($[11] !== activeFilter) {
      t9 = function getCountryClassName(id) {
        const isHome = id === "348";
        const isVisited = VISITED_IDS.includes(id);
        let colorClass;
        if (isHome) {
          colorClass = "fill-accent";
        } else {
          if (isVisited) {
            colorClass = "fill-muted/80";
          } else {
            colorClass = "fill-muted/20";
          }
        }
        let opacityClass = "opacity-100";
        if (activeFilter === "home" && !isHome) {
          opacityClass = "opacity-20";
        } else {
          if (activeFilter === "visited" && !isVisited) {
            opacityClass = "opacity-20";
          }
        }
        return `${"transition-all duration-300 stroke-background stroke-[1px] outline-none hover:fill-muted cursor-pointer"} ${colorClass} ${opacityClass}`;
      };
      $[11] = activeFilter;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    const getCountryClassName = t9;
    let t10;
    let t11;
    let t12;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = () => setActiveFilter("home");
      t11 = () => setActiveFilter(null);
      t12 = <div className="mr-1 size-2 rounded-full border border-muted/40 bg-accent" />;
      $[13] = t10;
      $[14] = t11;
      $[15] = t12;
    } else {
      t10 = $[13];
      t11 = $[14];
      t12 = $[15];
    }
    let t13;
    if ($[16] !== t) {
      t13 = t("home");
      $[16] = t;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    let t14;
    if ($[18] !== t13) {
      t14 = <Chip className="cursor-default" variant="primary" color="accent" onMouseEnter={t10} onMouseLeave={t11}>{t12}{t13}</Chip>;
      $[18] = t13;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    let t15;
    let t16;
    let t17;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = () => setActiveFilter("visited");
      t16 = () => setActiveFilter(null);
      t17 = <div className="mr-1 size-2 rounded-full border border-muted/40 bg-muted" />;
      $[20] = t15;
      $[21] = t16;
      $[22] = t17;
    } else {
      t15 = $[20];
      t16 = $[21];
      t17 = $[22];
    }
    let t18;
    if ($[23] !== t) {
      t18 = t("visited");
      $[23] = t;
      $[24] = t18;
    } else {
      t18 = $[24];
    }
    let t19;
    if ($[25] !== t18) {
      t19 = <Chip className="cursor-default" onMouseEnter={t15} onMouseLeave={t16}>{t17}{t18}</Chip>;
      $[25] = t18;
      $[26] = t19;
    } else {
      t19 = $[26];
    }
    if ($[27] !== t14 || $[28] !== t19) {
      t8 = <div className="mb-4 flex space-x-2">{t14}{t19}</div>;
      $[27] = t14;
      $[28] = t19;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    t3 = "block h-auto w-full";
    t4 = "0 0 1000 650";
    t5 = "http://www.w3.org/2000/svg";
    t6 = "xMidYMid meet";
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <title>World Map</title>;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    t1 = "world-map-paths";
    t2 = featureCollection.features.map((feature, index) => {
      const d = pathGenerator(feature.geometry);
      if (!d) {
        return null;
      }
      return <path className={getCountryClassName(feature.id)} d={d} key={`${feature.id}-${index}`}><title>{feature.properties.name}</title></path>;
    });
    $[1] = activeFilter;
    $[2] = t;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[31] !== t1 || $[32] !== t2) {
    t9 = <g id={t1}>{t2}</g>;
    $[31] = t1;
    $[32] = t2;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t3 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t9) {
    t10 = <svg className={t3} viewBox={t4} xmlns={t5} preserveAspectRatio={t6}>{t7}{t9}</svg>;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10 || $[42] !== t8) {
    t11 = <div>{t8}{t10}</div>;
    $[41] = t10;
    $[42] = t8;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
}